import React, { useState, useEffect, useRef } from 'react';
import api from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Plus, Trash2, ChevronRight, X, FileText, Check, Upload, BookOpen } from 'lucide-react';
import ContractEditor from '../../../components/ContractEditor';
import TypeAheadLookup from '../../../components/TypeAheadLookup';
import { LoadingSpinner } from '../../../components/layout/SharedComponents';
import { CAT_COLOR } from '../contractConstants';

export function TemplatePicker({ onSelect, onBlank }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('All');

  useEffect(() => {
    api.get('/api/contracts/templates')
      .then(r => setTemplates(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const categories = ['All', ...new Set(templates.map(t => t.category).filter(Boolean))];
  const filtered = templates.filter(t => {
    const s = search.toLowerCase();
    return (
      (filterCat === 'All' || t.category === filterCat) &&
      (!s || t.name.toLowerCase().includes(s) || (t.tags || []).some(tag => tag.toLowerCase().includes(s)))
    );
  });

  return (
    <div className="space-y-5">
      <div className="text-center pb-2">
        <BookOpen className="h-10 w-10 text-primary mx-auto mb-2" />
        <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Choose a Contract Template</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Select from the standard library below, or start from a blank contract.
        </p>
      </div>

      {/* Blank option */}
      <button
        onClick={onBlank}
        className="w-full flex items-center gap-3 border-2 border-dashed border-border rounded-xl px-4 py-3 hover:border-primary hover:bg-primary/5 transition-colors text-left"
      >
        <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
          <FileText className="h-5 w-5 text-muted-foreground" />
        </div>
        <div>
          <p className="font-semibold text-sm">Blank Contract</p>
          <p className="text-xs text-muted-foreground">Start with an empty document and write from scratch</p>
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto" />
      </button>

      {/* Search + category filter */}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search templates..."
          className="h-8 w-48"
        />
        <div className="flex flex-wrap gap-1">
          {categories.map(cat => (
            <button key={cat} onClick={() => setFilterCat(cat)}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${filterCat === cat ? 'bg-primary text-white border-primary' : 'border-border hover:bg-accent'}`}>
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Template grid */}
      {loading ? <div className="py-8"><LoadingSpinner /></div> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[420px] overflow-y-auto pr-1">
          {filtered.map(t => {
            const catCls = CAT_COLOR[t.category] || 'bg-gray-50 border-gray-200 text-gray-700';
            return (
              <button
                key={t.id}
                onClick={() => onSelect(t)}
                className="text-left border rounded-xl p-4 hover:border-primary hover:shadow-sm hover:bg-primary/5 transition-all flex flex-col gap-2"
              >
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold text-sm leading-snug">{t.name}</h3>
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border flex-shrink-0 ${catCls}`}>{t.category}</span>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">{t.description}</p>
                {t.tags && t.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {t.tags.slice(0, 3).map(tag => (
                      <span key={tag} className="text-[10px] bg-muted px-1.5 py-0.5 rounded">{tag}</span>
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-end mt-1">
                  <span className="text-xs font-medium text-primary flex items-center gap-1">
                    Use this template <ChevronRight className="h-3 w-3" />
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function SupplierSelect({ supplierCode, onSelect }) {
  const [search, setSearch] = useState(supplierCode || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (search.length < 1) { setResults([]); return; }
      try {
        const res = await api.get('/api/data/suppliers', { params: { search, page_size: 15 } });
        setResults(res.data.data || []);
        setOpen(true);
      } catch {}
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <Input value={search} onChange={e => { setSearch(e.target.value); if (!e.target.value) onSelect('', ''); }}
        placeholder="Type supplier code or name..." onFocus={() => search && results.length && setOpen(true)} />
      {open && results.length > 0 && (
        <div className="absolute z-50 w-full bg-card border rounded-xl shadow-lg mt-1 max-h-48 overflow-y-auto">
          {results.map(s => (
            <button key={s.supplier_code} className="w-full flex items-start gap-2 px-3 py-2 hover:bg-accent text-left"
              onClick={() => { onSelect(s.supplier_code, s.supplier_name); setSearch(s.supplier_code); setOpen(false); }}>
              <span className="font-mono text-xs text-primary font-semibold w-24 flex-shrink-0">{s.supplier_code}</span>
              <span className="text-sm">{s.supplier_name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ContractManagerSelect({ value, onChange }) {
  const [users, setUsers] = useState([]);
  useEffect(() => {
    api.get('/api/users', { params: { page_size: 200 } })
      .then(r => setUsers(r.data.data || [])).catch(() => {});
  }, []);
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none">
      <option value="">-- Select Contract Manager --</option>
      {users.map(u => (
        <option key={u.user_id} value={`${u.first_name} ${u.last_name}`.trim()}>
          {u.first_name} {u.last_name} ({u.user_id})
        </option>
      ))}
    </select>
  );
}

export function ApprovalRouteBuilder({ route, onChange }) {
  const [allUsers, setAllUsers] = useState([]);
  const [search, setSearch] = useState('');
  useEffect(() => {
    api.get('/api/users', { params: { page_size: 200 } })
      .then(r => setAllUsers(r.data.data || [])).catch(() => {});
  }, []);
  const add = (u) => {
    if (route.find(a => a.user_id === u.user_id)) return;
    onChange([...route, { user_id: u.user_id, name: `${u.first_name} ${u.last_name}`.trim(), email: u.email, order: route.length + 1 }]);
  };
  const remove = (uid) => onChange(route.filter(a => a.user_id !== uid).map((a, i) => ({ ...a, order: i + 1 })));
  const filtered = allUsers.filter(u => { const s = search.toLowerCase(); return !s || `${u.first_name} ${u.last_name} ${u.user_id}`.toLowerCase().includes(s); });
  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <p className="text-xs font-semibold mb-2 text-muted-foreground uppercase tracking-wide">Approval Order</p>
        {route.length === 0 ? (
          <div className="border-2 border-dashed rounded-xl p-4 text-center text-xs text-muted-foreground">No approvers — contracts can still be submitted but won't require sign-off.</div>
        ) : (
          <div className="space-y-1.5">
            {route.map((a, i) => (
              <div key={a.user_id} className="flex items-center gap-2 p-2.5 border rounded-xl">
                <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">{i + 1}</span>
                <div className="flex-1 min-w-0"><p className="text-sm font-medium">{a.name}</p><p className="text-xs text-muted-foreground">{a.email}</p></div>
                <button onClick={() => remove(a.user_id)} className="text-destructive" aria-label="Remove"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <p className="text-xs font-semibold mb-2 text-muted-foreground uppercase tracking-wide">Add Approvers</p>
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search users..." className="h-8 mb-2" />
        <div className="border rounded-xl overflow-hidden max-h-48 overflow-y-auto">
          {filtered.slice(0, 20).map(u => {
            const added = route.find(a => a.user_id === u.user_id);
            return (
              <div key={u.user_id} className="flex items-center justify-between px-3 py-2 border-b last:border-0 hover:bg-accent">
                <div><p className="text-xs font-medium">{u.first_name} {u.last_name}</p><p className="text-[10px] text-muted-foreground">{u.email}</p></div>
                <Button size="sm" variant={added ? 'secondary' : 'outline'} disabled={!!added} onClick={() => add(u)} className="h-6 text-xs px-2">{added ? '✓' : '+ Add'}</Button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function ContractDetailsStep({ form, onChange, template }) {
  const f = (k, v) => onChange(prev => ({ ...prev, [k]: v }));

  // Auto-calculate review date = end date minus notice_period days
  const handleEndDateChange = (endDate) => {
    f('contract_end', endDate);
    if (endDate && form.notice_period) {
      const end = new Date(endDate);
      const review = new Date(end.getTime() - form.notice_period * 86400000);
      f('review_date', review.toISOString().split('T')[0]);
    }
  };

  return (
    <div className="space-y-4">
      {template && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/5 border border-primary/20">
          <BookOpen className="h-4 w-4 text-primary flex-shrink-0" />
          <p className="text-sm">Template: <strong>{template.name}</strong></p>
          <Badge variant="outline" className="ml-auto text-xs">{template.category}</Badge>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Contract Number *</Label>
          <Input value={form.contract_number} onChange={e => f('contract_number', e.target.value)}
            placeholder="e.g. MSA-2025-001" />
        </div>
        <div className="space-y-1.5">
          <Label>Description *</Label>
          <Input value={form.description} onChange={e => f('description', e.target.value)}
            placeholder="Brief description" />
        </div>

        {/* Supplier Code — searchable from DB, auto-fills name */}
        <div className="space-y-1.5">
          <Label>Supplier Code</Label>
          <TypeAheadLookup
            endpoint="/api/data/suppliers"
            valueField="supplier_code"
            labelField="supplier_name"
            value={form.supplier_code}
            displayValue={form.supplier_code}
            placeholder="Search suppliers by code or name..."
            onChange={(code, rec) => { f('supplier_code', code); f('supplier_name', rec?.supplier_name || ''); }}
          />
        </div>
        <div className="space-y-1.5">
          <Label>Supplier Name</Label>
          <Input value={form.supplier_name} readOnly className="bg-muted/40 text-sm"
            placeholder="Auto-filled from supplier code" />
        </div>

        <div className="space-y-1.5">
          <Label>Contract Value</Label>
          <div className="flex gap-2">
            <Input type="number" value={form.contract_value} onChange={e => f('contract_value', parseFloat(e.target.value) || 0)} className="flex-1" />
            <div className="w-28 flex-shrink-0">
              <TypeAheadLookup
                endpoint="/api/data/currencies"
                valueField="code"
                labelField="description"
                value={form.currency || 'USD'}
                displayValue={form.currency || 'USD'}
                placeholder="USD"
                onChange={(code) => f('currency', code)}
              />
            </div>
          </div>
        </div>

        {/* Contract Manager — dropdown from users DB */}
        <div className="space-y-1.5">
          <Label>Contract Manager</Label>
          <ContractManagerSelect value={form.contract_manager} onChange={v => f('contract_manager', v)} />
        </div>

        <div className="space-y-1.5">
          <Label>Start Date <span className="text-xs text-muted-foreground">(defaults to today)</span></Label>
          <Input type="date" value={form.contract_start} onChange={e => f('contract_start', e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>End Date</Label>
          <Input type="date" value={form.contract_end} onChange={e => handleEndDateChange(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>Review Date <span className="text-xs text-muted-foreground">(auto: {form.notice_period}d before end)</span></Label>
          <Input type="date" value={form.review_date} onChange={e => f('review_date', e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>Notice Period (days)</Label>
          <Input type="number" value={form.notice_period}
            onChange={e => {
              const np = parseInt(e.target.value) || 90;
              f('notice_period', np);
              if (form.contract_end) {
                const end = new Date(form.contract_end);
                const review = new Date(end.getTime() - np * 86400000);
                f('review_date', review.toISOString().split('T')[0]);
              }
            }} />
        </div>
        <div className="space-y-1.5">
          <Label>Payment Terms</Label>
          <Input value={form.payment_terms} onChange={e => f('payment_terms', e.target.value)} placeholder="Net 30" />
        </div>
        <div className="space-y-1.5">
          <Label>Governing Law <span className="text-xs text-muted-foreground">(optional)</span></Label>
          <Input value={form.governing_law} onChange={e => f('governing_law', e.target.value)} placeholder="e.g. England & Wales" />
        </div>
      </div>

      {/* Approval Route */}
      <div className="border rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold">Approval Route</h3>
          <span className="text-xs text-muted-foreground">Add approvers below or use the system default route</span>
        </div>
        <ApprovalRouteBuilder route={form.approval_route || []} onChange={v => f('approval_route', v)} />
      </div>
    </div>
  );
}

export function ContractWritingStep({ body, onChange, contractNumber, contractData, isReadOnly }) {
  return (
    <div className="space-y-2">
      {isReadOnly && (
        <div className="px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-700">
          Contract is Under Review or Approved — content is read-only.
        </div>
      )}
      <ContractEditor content={body} onChange={onChange} readOnly={isReadOnly} contractNumber={contractNumber} contractData={contractData} />
    </div>
  );
}

export function ApprovalHistory({ history, fmtTz }) {
  if (!history.length)
    return <p className="text-sm text-muted-foreground py-4 text-center">No approval history yet.</p>;
  const A = { SUBMITTED: 'bg-blue-100 text-blue-700', APPROVED: 'bg-green-100 text-green-700', STAGE_APPROVED: 'bg-green-100 text-green-700', REJECTED: 'bg-red-100 text-red-700' };
  const actionLabel = (h) => {
    const stage = h.stage_label || h.stage || '';
    if (h.action === 'SUBMITTED') return `${stage} submitted`;
    if (h.action === 'APPROVED' || h.action === 'STAGE_APPROVED') return `${stage} approved`;
    if (h.action === 'REJECTED') return `${stage} rejected`;
    return `${stage} ${h.action}`;
  };
  return (
    <div className="space-y-3">
      {history.map((h, i) => (
        <div key={i} className="flex items-start gap-3 p-3 rounded-lg border bg-muted/20">
          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap ${A[h.action] || 'bg-gray-100 text-gray-600'}`}>
            {actionLabel(h)}
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm">{h.note || <span className="text-muted-foreground italic">No note</span>}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {h.submitted_by || h.reviewed_by} · {h.created_at ? (fmtTz ? fmtTz(h.created_at) : new Date(h.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Los_Angeles' })) : ''}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}
